import { useCallback, useRef } from 'react'
import mapboxgl from 'mapbox-gl'
import { Feature, PropertyMarker } from './Map.types'
import { getEventFeature } from './Map.util'

interface UseMapHandlersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  popup: React.MutableRefObject<mapboxgl.Popup | null>
  mapLoaded: boolean
  data: any[]
  onFeatureClick?: (feature: Feature, e: mapboxgl.MapLayerMouseEvent) => void
  onFeatureHover?: (feature: Feature, e: mapboxgl.MapLayerMouseEvent) => void
  onMarkerClick?: (marker: PropertyMarker) => void
  setHoverFeature: (feature: Feature | undefined) => void
  setClickFeature: (feature: Feature | undefined) => void
}

export const useMapHandlers = ({
  map,
  popup,
  mapLoaded,
  data,
  onFeatureClick,
  onFeatureHover,
  onMarkerClick,
  setHoverFeature,
  setClickFeature,
}: UseMapHandlersProps) => {
  // Track marker instances for cleanup
  const markersRef = useRef<mapboxgl.Marker[]>([])

  // Mouse event handlers
  const handleMouseMove = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      const feature = getEventFeature(e)
      if (!feature || !map.current) return

      map.current.getCanvas().style.cursor = 'pointer'
      setHoverFeature(feature)
      onFeatureHover?.(feature, e)
    },
    [map, setHoverFeature, onFeatureHover]
  )

  const handleMouseLeave = useCallback(() => {
    if (!map.current) return
    map.current.getCanvas().style.cursor = ''
    setHoverFeature(undefined)
    popup.current?.remove()
  }, [map, popup, setHoverFeature])

  const handleClick = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      const feature = getEventFeature(e)
      if (!feature) return

      setClickFeature(feature)
      onFeatureClick?.(feature, e)
    },
    [setClickFeature, onFeatureClick]
  )

  // Enable/disable map events
  const enableMapEvents = useCallback(() => {
    if (!map.current || !mapLoaded) return

    data.forEach((source) => {
      source.layers.forEach((layer: any) => {
        if (layer.hover) {
          map.current!.on('mousemove', layer.id, handleMouseMove)
          map.current!.on('mouseleave', layer.id, handleMouseLeave)
        }
        if (layer.click) {
          map.current!.on('click', layer.id, handleClick)
        }
      })
    })
  }, [data, mapLoaded, handleMouseMove, handleMouseLeave, handleClick])

  const disableMapEvents = useCallback(() => {
    if (!map.current || !mapLoaded) return

    data.forEach((source) => {
      source.layers.forEach((layer: any) => {
        map.current!.off('mousemove', layer.id, handleMouseMove)
        map.current!.off('mouseleave', layer.id, handleMouseLeave)
        map.current!.off('click', layer.id, handleClick)
      })
    })
  }, [data, mapLoaded, handleMouseMove, handleMouseLeave, handleClick])

  // Format price for display
  const formatMarkerPrice = (price?: number): string => {
    if (!price) return ''
    if (price >= 1000000) {
      return `${(price / 1000000).toFixed(1)}M`
    }
    return `${(price / 1000).toFixed(0)}K`
  }

  // Remove all markers
  const removeMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => marker.remove())
    markersRef.current = []

    // Clean up tooltips from DOM
    const tooltips = document.querySelectorAll('.marker-tooltip')
    tooltips.forEach((tooltip) => tooltip.remove())
  }, [])

  // Create tooltip element
  const createTooltip = useCallback((marker: PropertyMarker) => {
    const tooltip = document.createElement('div')
    tooltip.className = 'marker-tooltip'
    tooltip.style.cssText = `
      position: fixed;
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      padding: 8px;
      min-width: 200px;
      max-width: 250px;
      pointer-events: none;
      opacity: 0;
      transition: opacity 150ms ease-out;
      z-index: 10000;
      display: none;
    `

    // Tooltip content
    const content = `
      <div style="display: flex; flex-direction: column; gap: 6px;">
        ${marker.image ? `<img src="${marker.image}" style="width: 100%; height: 100px; object-fit: cover; border-radius: 4px;" />` : ''}
        <div style="font-size: 13px; font-weight: 600; color: #1a1a1a;">${marker.title || 'Property'}</div>
        ${marker.price ? `<div style="font-size: 14px; font-weight: 600; color: #00ade6;">${formatMarkerPrice(marker.price)} CHF</div>` : ''}
        ${marker.properties?.area ? `<div style="font-size: 12px; color: #666;">${marker.properties.area}</div>` : ''}
      </div>
    `
    tooltip.innerHTML = content
    return tooltip
  }, [])

  // Add markers
  const addMarkers = useCallback(
    (markers: PropertyMarker[]) => {
      if (!map.current || !mapLoaded) return

      // Clean up existing markers
      removeMarkers()

      markers.forEach((marker) => {
        // Create custom marker element
        const el = document.createElement('div')
        el.className = 'custom-marker'
        el.style.cssText = `
          cursor: pointer;
          user-select: none;
        `

        // Create tooltip and append to body (not to marker element)
        const tooltip = createTooltip(marker)
        document.body.appendChild(tooltip)

        // Position tooltip function
        const positionTooltip = () => {
          const rect = el.getBoundingClientRect()
          tooltip.style.left = `${rect.left + rect.width / 2}px`
          tooltip.style.top = `${rect.top - 8}px`
          tooltip.style.transform = 'translate(-50%, -100%)'
        }

        // Use custom icon if provided
        if (marker.iconUrl) {
          const iconImg = document.createElement('img')
          const [iconWidth, iconHeight] = marker.iconSize || [32, 40]
          iconImg.src = marker.iconUrl
          iconImg.style.cssText = `
            width: ${iconWidth}px;
            height: ${iconHeight}px;
            object-fit: contain;
            pointer-events: none;
            transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1);
          `
          el.appendChild(iconImg)

          // Add hover effect for custom icon
          el.addEventListener('mouseenter', () => {
            iconImg.style.transform = 'scale(1.1)'
            positionTooltip()
            tooltip.style.display = 'block'
            tooltip.style.opacity = '1'
          })

          el.addEventListener('mouseleave', () => {
            iconImg.style.transform = 'scale(1)'
            tooltip.style.opacity = '0'
            setTimeout(() => {
              tooltip.style.display = 'none'
            }, 150)
          })
        } else if (marker.price) {
          // Create price label if price exists
          const priceLabel = document.createElement('div')
          priceLabel.className = 'marker-price-label'
          priceLabel.textContent = `${formatMarkerPrice(marker.price)} CHF`
          priceLabel.style.cssText = `
            background-color: #ffffff;
            color: #1a1a1a;
            padding: 4px 10px;
            border-radius: 16px;
            font-size: 12px;
            font-weight: 600;
            white-space: nowrap;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            border: 2px solid ${marker.highlighted ? '#0088b8' : '#00ade6'};
            pointer-events: none;
          `
          el.appendChild(priceLabel)

          // Add hover effect
          el.addEventListener('mouseenter', () => {
            priceLabel.style.transform = 'scale(1.05)'
            priceLabel.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)'
            priceLabel.style.borderColor = '#0088b8'
            positionTooltip()
            tooltip.style.display = 'block'
            tooltip.style.opacity = '1'
          })

          el.addEventListener('mouseleave', () => {
            if (!marker.highlighted) {
              priceLabel.style.transform = 'scale(1)'
              priceLabel.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)'
              priceLabel.style.borderColor = '#00ade6'
            }
            tooltip.style.opacity = '0'
            setTimeout(() => {
              tooltip.style.display = 'none'
            }, 150)
          })

          // Update style when highlighted
          if (marker.highlighted) {
            priceLabel.style.transform = 'scale(1.1)'
            priceLabel.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.25)'
            priceLabel.style.borderWidth = '3px'
          }
        } else {
          // Fallback to default pin marker if no price
          const pin = document.createElement('div')
          pin.style.cssText = `
            width: 16px;
            height: 16px;
            background-color: ${marker.highlighted ? '#0088b8' : '#00ade6'};
            border: 2px solid #ffffff;
            border-radius: 50%;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
            transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
          `
          el.appendChild(pin)

          // Add hover effect for pin
          el.addEventListener('mouseenter', () => {
            pin.style.transform = 'scale(1.2)'
            pin.style.backgroundColor = '#0088b8'
            positionTooltip()
            tooltip.style.display = 'block'
            tooltip.style.opacity = '1'
          })

          el.addEventListener('mouseleave', () => {
            if (!marker.highlighted) {
              pin.style.transform = 'scale(1)'
              pin.style.backgroundColor = '#00ade6'
            }
            tooltip.style.opacity = '0'
            setTimeout(() => {
              tooltip.style.display = 'none'
            }, 150)
          })
        }

        const markerElement = new mapboxgl.Marker({
          element: el,
          anchor: 'bottom',
        })
          .setLngLat(marker.coordinates)
          .addTo(map.current!)

        // Store marker instance for cleanup
        markersRef.current.push(markerElement)

        if (onMarkerClick) {
          el.addEventListener('click', (e) => {
            e.stopPropagation()
            onMarkerClick(marker)
          })
        }
      })
    },
    [map, mapLoaded, onMarkerClick, removeMarkers, createTooltip]
  )

  return {
    handleMouseMove,
    handleMouseLeave,
    handleClick,
    enableMapEvents,
    disableMapEvents,
    addMarkers,
    removeMarkers,
  }
}
